'use client';

import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  WatchlistIdsResponse,
  WatchlistResponse,
} from '@/lib/backend-api';
import {
  InsufficientCreditsError,
  throwIfInsufficientCredits,
} from '@/hooks/use-alerts';
import { useSession } from '@/hooks/use-session';

const WATCHLIST_IDS_KEY = ['watchlist', 'ids'] as const;
const WATCHLIST_FULL_KEY = ['watchlist', 'full'] as const;

async function fetchWatchlistIds(): Promise<WatchlistIdsResponse> {
  const res = await fetch('/api/watchlist/ids');
  if (!res.ok) throw new Error(`/api/watchlist/ids returned ${res.status}`);
  const json = await res.json();
  return json.data as WatchlistIdsResponse;
}

async function fetchWatchlistFull(): Promise<WatchlistResponse> {
  const res = await fetch('/api/watchlist');
  if (!res.ok) throw new Error(`/api/watchlist returned ${res.status}`);
  const json = await res.json();
  return json.data as WatchlistResponse;
}

async function addWatchlistRequest(euId: string): Promise<void> {
  const res = await fetch(`/api/watchlist/${encodeURIComponent(euId)}`, {
    method: 'POST',
  });
  // Saving is free (the backend stores a disabled watch). A 402 is kept as
  // a defensive path in case an older backend still charges for the star.
  await throwIfInsufficientCredits(res);
  if (!res.ok)
    throw new Error(`POST /api/watchlist/${euId} returned ${res.status}`);
}

async function removeWatchlistRequest(euId: string): Promise<void> {
  const res = await fetch(`/api/watchlist/${encodeURIComponent(euId)}`, {
    method: 'DELETE',
  });
  if (!res.ok)
    throw new Error(`DELETE /api/watchlist/${euId} returned ${res.status}`);
}

/**
 * Lightweight hook: exposes membership + unread count via the IDs-only
 * endpoint (no JOIN). Used by the per-company watchlist button and the
 * header notification menu.
 *
 * When `euId` is provided, `isWatched` reflects membership for that company
 * and `toggle` flips it. Mutations optimistically update the cache so the
 * button flips immediately without waiting for the server round-trip.
 */
export function useWatchlist(euId?: string) {
  const { data: session } = useSession();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<WatchlistIdsResponse>({
    queryKey: WATCHLIST_IDS_KEY,
    queryFn: fetchWatchlistIds,
    enabled: !!session,
    staleTime: 60 * 60 * 1000,
  });

  const euIds = useMemo(() => data?.eu_ids ?? [], [data]);
  const isWatched = useMemo(
    () => (euId ? euIds.includes(euId) : false),
    [euIds, euId],
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: WATCHLIST_IDS_KEY });
    queryClient.invalidateQueries({ queryKey: WATCHLIST_FULL_KEY });
    // Saving creates (and unsaving deletes) the company's watch row, which
    // the bell and the subscribe dialog read through `useCompanyWatch`.
    queryClient.invalidateQueries({ queryKey: ['watch'] });
    queryClient.invalidateQueries({ queryKey: ['alerts'] });
  };

  const optimisticAdd = (id: string) => {
    queryClient.setQueryData<WatchlistIdsResponse>(
      WATCHLIST_IDS_KEY,
      (prev) => {
        const base: WatchlistIdsResponse = prev ?? {
          eu_ids: [],
          unread_notification_count: 0,
        };
        if (base.eu_ids.includes(id)) return base;
        return { ...base, eu_ids: [id, ...base.eu_ids] };
      },
    );
  };

  const optimisticRemove = (id: string) => {
    queryClient.setQueryData<WatchlistIdsResponse>(
      WATCHLIST_IDS_KEY,
      (prev) => {
        if (!prev) return prev;
        return { ...prev, eu_ids: prev.eu_ids.filter((e) => e !== id) };
      },
    );
  };

  const addMutation = useMutation({
    mutationFn: addWatchlistRequest,
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: WATCHLIST_IDS_KEY });
      const previous =
        queryClient.getQueryData<WatchlistIdsResponse>(WATCHLIST_IDS_KEY);
      optimisticAdd(id);
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(WATCHLIST_IDS_KEY, ctx.previous);
      }
    },
    onSettled: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: removeWatchlistRequest,
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: WATCHLIST_IDS_KEY });
      const previous =
        queryClient.getQueryData<WatchlistIdsResponse>(WATCHLIST_IDS_KEY);
      optimisticRemove(id);
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(WATCHLIST_IDS_KEY, ctx.previous);
      }
    },
    onSettled: invalidate,
  });

  /**
   * Flip membership (save / unsave). Saving is free; `'credits'` is only a
   * defensive outcome for a 402, returned rather than thrown because a
   * rejected promise here would surface as an unhandled error in every
   * caller. Subscribing to updates is a separate, priced step
   * (`useCompanyWatch().save` with `enabled: true`).
   */
  const toggle = useCallback(async (): Promise<
    'added' | 'removed' | 'credits' | null
  > => {
    if (!session || !euId) return null;
    if (isWatched) {
      await removeMutation.mutateAsync(euId);
      return 'removed';
    }
    try {
      await addMutation.mutateAsync(euId);
      return 'added';
    } catch (error) {
      if (error instanceof InsufficientCreditsError) return 'credits';
      throw error;
    }
  }, [session, euId, isWatched, addMutation, removeMutation]);

  return {
    euIds,
    isLoading,
    isWatched,
    toggle,
    isMutating: addMutation.isPending || removeMutation.isPending,
    unreadNotificationCount: data?.unread_notification_count ?? 0,
  };
}

/**
 * Full hook with per-item company details (name/register/sitz). Used by the
 * watchlist listing page. Falls back to IDs-cache for add/remove invalidation.
 */
export function useWatchlistFull() {
  const { data: session } = useSession();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery<WatchlistResponse>({
    queryKey: WATCHLIST_FULL_KEY,
    queryFn: fetchWatchlistFull,
    enabled: !!session,
    staleTime: 60 * 60 * 1000,
  });

  const items = useMemo(() => data?.items ?? [], [data]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: WATCHLIST_IDS_KEY });
    queryClient.invalidateQueries({ queryKey: WATCHLIST_FULL_KEY });
  };

  const removeMutation = useMutation({
    mutationFn: removeWatchlistRequest,
    onSuccess: invalidate,
  });

  return {
    items,
    isLoading,
    // Surfaced so the page can offer a retry rather than spinning forever
    // when the API is down.
    isError,
    refetch,
    remove: (euId: string) => removeMutation.mutateAsync(euId),
    isRemoving: removeMutation.isPending,
    unreadNotificationCount: data?.unread_notification_count ?? 0,
  };
}
